import { Component, Output, EventEmitter, OnDestroy, Input } from '@angular/core';
import 'moment/locale/id';
import * as moment from 'moment';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BaseListBComponent } from '../../../libraries/base/list/base-list.bcomponent';
import { ExportDataTableService } from '../../../libraries/export/export-data-table.service';
import { AuthenticationService } from '../../../libraries/auth/authentication.service';
import { DashboardRestService } from '../dashboard-rest.service';
import { formatRupiah } from '../../../shared/utils';
import { DashboardCommunicationService } from '../dashboard-communication.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-transaction-history-table',
  templateUrl: 'transaction-history-table.component.html'
})
export class TransactionHistoryTableComponent extends BaseListBComponent<any> implements OnDestroy {
  @Output() totalRentalPriceChange = new EventEmitter<number>();
  @Input() categoryOption: { text: string; value: string }[] = [];

  formatRupiah = formatRupiah;
  totalRentalPrice: number = 0; // Example total rental price, replace with actual data fetching logic
  private subscription: Subscription = new Subscription();
  isRetryLoading: boolean = false;

  tableColumns = [
    {
      i18nLabel: 'ui.dashboard.entity.no',
      field: 'no',
      name: 'No',
      sort: true // kalau bisa sort
    },
    {
      i18nLabel: 'ui.dashboard.entity.refNumber',
      field: 'referenceNumber',
      name: '',
      sort: true // kalau bisa sort
    },
    {
      i18nLabel: 'ui.dashboard.entity.category',
      field: '',
      name: 'category',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.transactionType',
      field: '',
      name: 'transactionType',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.status',
      field: '',
      name: 'status',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.fee',
      field: '',
      name: 'fee',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.subTotal',
      field: '',
      name: 'subTotal',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.total',
      field: '',
      name: 'total',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.transactionDate',
      field: 'createdAt',
      name: '',
      sort: false
    },
    // TODO: tambahin field paid date
    {
      i18nLabel: 'ui.dashboard.entity.paidDate',
      field: 'paidDate',
      name: '',
      sort: false
    },
    {
      i18nLabel: 'ui.dashboard.entity.action',
      field: '',
      name: 'action',
      sort: false
    }
  ];
  tableColumnsShow: boolean = false;
  tableColumnsToggle = this._gridTableToggle.mapToggleOptionsFromColumns(this.tableColumns);
  isLoadingExportExcel: boolean = false;

  constructor(
    public _authentication: AuthenticationService,
    private _DashboardRest: DashboardRestService,
    private _export: ExportDataTableService,
    private _dashboardCommunication: DashboardCommunicationService
  ) {
    super();
    this.componentId = 'TransactionHistoryTableComponent';
    this.qParams.transactionType = '';
    this.qParams.category = '';
    // this.headerTitle = 'ui.gojekSwadaya.list.title';

    // Subscribe to refresh events
    this.subscription.add(
      this._dashboardCommunication.refreshTransactionHistory$.subscribe(() => {
        this.callHookDirectly('init');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
    );
  }

  appDefineFixedHooks() {
    super.appDefineFixedHooks();

    this.registerHook({
      hookName: 'loadData',
      handle: event => {
        const qOption = event.data.qOption;
        qOption['limit'] = qOption['take'];
        delete qOption['take'];
        delete qOption['skip'];
        const qParams = Object.assign({}, event.data.qParams, qOption, this.qParams);
        return this._DashboardRest.getTransactionHistory(qParams).pipe(
          switchMap(doc => {
            const dataFormatted = doc?.data?.map(d => {
              d.createdAt = d.createdAt ? moment(d?.createdAt).format('DD MMMM YYYY HH:mm') : '-';
              d.updatedAt = d.updatedAt ? moment(d?.updatedAt).format('DD MMMM YYYY HH:mm') : '-';
              d.paidDate = d.paidDate ? moment(d?.paidDate).format('DD MMMM YYYY HH:mm') : '-';
              return d;
            });

            doc.data = dataFormatted;
            this.totalRentalPrice = doc.totalRentalPrice;
            this.totalRentalPriceChange.emit(this.totalRentalPrice);
            return of(doc);
          })
        );
      },
      wrapRetryableTask: true,
      wrapErrorMessage: true
    });

    this.registerHook({
      hookName: 'init',
      handle: () => this.page.compHookService?.callMultipleHooks(['TransactionHistoryTableComponent:workflowLoadData'])
    });
  }

  typeOption = [
    {
      text: 'Semua Tipe Transaksi',
      value: ''
    },
    {
      text: 'Debit',
      value: 'debit'
    },
    {
      text: 'Credit',
      value: 'credit'
    }
  ];

  appOnInit() {
    super.appOnInit();

    return this.callHook('init');
  }

  mapCategory(category: string): string {
    const categoryMap: Record<string, string> = {
      TOP_UP: 'Top Up',
      WITHDRAW: 'Withdraw',
      PROFIT_SHARING: 'Profit Sharing',
      MEMBERSHIP_PAYMENT: 'Membership Payment'
    };

    return categoryMap[category] ?? 'Default';
  }

  getStatusColor(status: string) {
    if (!status) return 'warning';

    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus == 'pending') return 'warning';
    if (normalizedStatus == 'success') return 'success';
    if (normalizedStatus == 'rejected') return 'danger';
  }

  async onRetryTransaction(id: string) {
    this.isRetryLoading = true;
    try {
      const result = await this._DashboardRest.retryTransaction(id).toPromise();
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Retry transaksi berhasil.',
      });
      this._dashboardCommunication.triggerTransactionHistoryRefresh();
    } catch (error) {
      console.error('Error retrying transaction:', error);
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Gagal retry transaksi. Silakan coba lagi.'
      });
    } finally {
      this.isRetryLoading = false;
    }
  }

  async onDownloadExcel() {
    this.isLoadingExportExcel = true;
    try {
      const result = await this._DashboardRest.getTransactionHistory({}).toPromise();
      const mapOption = {
        referenceNumber: { header: 'Reference Number' },
        category: { header: 'Category' },
        transactionType: { header: 'Transaction Type' },
        status: { header: 'Status' },
        fee: { header: 'Fee' },
        subTotal: { header: 'Sub Total' },
        total: { header: 'Total' },
        createdAt: { header: 'Transaction Date' },
        paidDate: { header: 'Paid Date' }
      };
      const payload = result.data.map(item => ({
        ...item,
        category: this.mapCategory(item?.category),
        fee: this.formatRupiah(item?.fee || 0),
        subTotal: this.formatRupiah(item?.subTotal || 0),
        total: this.formatRupiah(item?.total || 0),
        createdAt: item.createdAt ? moment(item?.createdAt).format('DD MMMM YYYY HH:mm') : '-',
        paidDate: item.paidDate ? moment(item?.paidDate).format('DD MMMM YYYY HH:mm') : '-'
      }));
      this._export.export({
        extension: 'xlsx',
        fileName: 'TransactionHistory',
        records: payload,
        mapOptions: mapOption,
        templateData: null,
        pdfOptions: null
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoadingExportExcel = false;
    }
  }
}
